import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Drawer,
  Input,
  InputNumber,
  Select,
  Spin,
  message
} from "antd";
import dayjs from "dayjs";
import {
  docExpPayAdvance,
  docExpPayLines,
  docExpPayReturn,
  docExpPaySettle,
  fetchDocExpPayPreview
} from "./doc-exp-api";
import { cntLineLabel, formatMoney, pick, toNumber } from "./doc-exp-utils";
import xSrc from "./assets/x.svg";

const EPS = 0.005;
const DRAWER_WIDTH_KEY = "planOnline.docExp.payDrawer.width";
const MIN_WIDTH = 400;
const MAX_WIDTH = 720;
const DEFAULT_WIDTH = 480;
const SCREEN_MARGIN = 80;

const maxAllowedWidth = () => {
  if (typeof window === "undefined") return MAX_WIDTH;
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, window.innerWidth - SCREEN_MARGIN));
};

const clampWidth = (value) => {
  const max = maxAllowedWidth();
  const n = Number(value);
  if (!Number.isFinite(n)) return Math.min(DEFAULT_WIDTH, max);
  return Math.min(max, Math.max(MIN_WIDTH, Math.round(n)));
};

const loadWidth = () => {
  try {
    return clampWidth(localStorage.getItem(DRAWER_WIDTH_KEY) ?? DEFAULT_WIDTH);
  } catch {
    return DEFAULT_WIDTH;
  }
};

const saveWidth = (value) => {
  try {
    localStorage.setItem(DRAWER_WIDTH_KEY, String(clampWidth(value)));
  } catch {
    /* ignore */
  }
};

const MODE_META = {
  advance: { title: "Принять аванс", ok: "Принять" },
  pay: { title: "Оплатить", ok: "Оплатить" },
  lines: { title: "Оплатить", ok: "Оплатить" },
  settle: { title: "Оплатить", ok: "Оплатить" },
  return: { title: "Возврат", ok: "Вернуть" }
};

const isPayMode = (m) => m === "pay" || m === "lines" || m === "settle";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const normalizeKassa = (row) => ({
  id: toNumber(pick(row, "id", "Id")),
  name: pick(row, "name", "Name") ?? ""
});

const normalizeLine = (row) => ({
  idStr: toNumber(pick(row, "idStr", "IdStr")),
  idTov: toNumber(pick(row, "idTov", "IdTov")) ?? 0,
  tovName: pick(row, "tovName", "TovName") ?? "",
  isAuto: toNumber(pick(row, "isAuto", "IsAuto")) ?? 0,
  symaExp: toNumber(pick(row, "symaExp", "SymaExp")) ?? 0,
  symaPlat: toNumber(pick(row, "symaPlat", "SymaPlat")) ?? 0,
  symaDolg: toNumber(pick(row, "symaDolg", "SymaDolg")) ?? 0
});

const normalizePreview = (data) => {
  const kassas = Array.isArray(data?.kassas ?? data?.Kassas)
    ? (data.kassas ?? data.Kassas).map(normalizeKassa).filter((x) => x.id)
    : [];
  const lines = Array.isArray(data?.lines ?? data?.Lines)
    ? (data.lines ?? data.Lines).map(normalizeLine).filter((x) => x.idStr)
    : [];
  return {
    syma: toNumber(pick(data, "syma", "Syma")) ?? 0,
    symaPlat: toNumber(pick(data, "symaPlat", "SymaPlat")) ?? 0,
    symaDolg: toNumber(pick(data, "symaDolg", "SymaDolg")) ?? 0,
    avansOpen: toNumber(pick(data, "avansOpen", "AvansOpen")) ?? 0,
    lineDebt: toNumber(pick(data, "lineDebt", "LineDebt")) ?? 0,
    cashNeed: toNumber(pick(data, "cashNeed", "CashNeed")) ?? 0,
    defaultIdKassa: toNumber(pick(data, "defaultIdKassa", "DefaultIdKassa")) ?? 0,
    kassas,
    lines
  };
};

/**
 * Операции оплаты путёвки: аванс, строки, зачёт, возврат.
 */
const DocExpPayDrawer = ({ open, mode, idDoc, onClose, onDone }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [idKassa, setIdKassa] = useState(null);
  const [fdate, setFdate] = useState(() => dayjs());
  const [komment, setKomment] = useState("");
  const [txtOsn, setTxtOsn] = useState("");
  const [syma, setSyma] = useState(0);
  const [checked, setChecked] = useState([]);
  const [amountByStr, setAmountByStr] = useState({});
  const [returnKind, setReturnKind] = useState("advance");
  const [useAvans, setUseAvans] = useState(true);
  const [size, setSize] = useState(loadWidth);
  const reqRef = useRef(0);

  const meta = MODE_META[mode] || MODE_META.advance;

  const resetLocal = useCallback(() => {
    setError("");
    setKomment("");
    setTxtOsn("");
    setFdate(dayjs());
    setChecked([]);
    setAmountByStr({});
    setReturnKind("advance");
    setUseAvans(true);
    setSyma(0);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onWinResize = () => setSize((prev) => clampWidth(prev));
    onWinResize();
    window.addEventListener("resize", onWinResize);
    return () => window.removeEventListener("resize", onWinResize);
  }, [open]);

  const onResize = useCallback((next) => {
    setSize(clampWidth(next));
  }, []);

  const onResizeEnd = useCallback(
    (finalSize) => {
      const next = clampWidth(typeof finalSize === "number" ? finalSize : size);
      setSize(next);
      saveWidth(next);
    },
    [size]
  );

  useEffect(() => {
    if (!open || !idDoc || !mode) return undefined;
    const requestId = ++reqRef.current;
    setLoading(true);
    setError("");
    setSubmitting(false);
    resetLocal();

    fetchDocExpPayPreview(idDoc)
      .then((data) => {
        if (requestId !== reqRef.current) return;
        const next = normalizePreview(data);
        setPreview(next);
        // «[Сертификат]» (id -1) по умолчанию не подставляем — сервер принимает только реальную кассу.
        const preferred =
          next.defaultIdKassa > 0 && next.kassas.some((k) => k.id === next.defaultIdKassa)
            ? next.defaultIdKassa
            : next.kassas.find((k) => k.id > 0)?.id ?? null;
        setIdKassa(preferred);

        if (mode === "advance") {
          setSyma(round2(Math.max(0, next.lineDebt - next.avansOpen)));
          setTxtOsn("Аванс по путёвке");
        } else if (isPayMode(mode)) {
          const debtLines = next.lines.filter((x) => x.symaDolg > EPS);
          setChecked(debtLines.map((x) => x.idStr));
          const amounts = {};
          debtLines.forEach((x) => {
            amounts[x.idStr] = round2(x.symaDolg);
          });
          setAmountByStr(amounts);
          setUseAvans(next.avansOpen > EPS);
          setTxtOsn("Оплата");
        } else if (mode === "return") {
          const hasAdvance = next.avansOpen > EPS;
          const platLines = next.lines.filter((x) => x.symaPlat > EPS);
          setReturnKind(hasAdvance || platLines.length === 0 ? "advance" : "lines");
          setSyma(round2(hasAdvance ? next.avansOpen : 0));
          setChecked(platLines.map((x) => x.idStr));
          const amounts = {};
          platLines.forEach((x) => {
            amounts[x.idStr] = round2(x.symaPlat);
          });
          setAmountByStr(amounts);
          setTxtOsn(hasAdvance ? "Возврат аванса" : "Возврат услуг");
        }
      })
      .catch((e) => {
        if (requestId !== reqRef.current) return;
        setPreview(null);
        setError(e.message || "Не удалось загрузить данные оплаты");
      })
      .finally(() => {
        if (requestId === reqRef.current) setLoading(false);
      });

    return undefined;
  }, [open, idDoc, mode, resetLocal]);

  const kassaOptions = useMemo(
    () => (preview?.kassas || []).map((k) => ({ value: k.id, label: k.name || `#${k.id}` })),
    [preview]
  );
  const kassaName = kassaOptions.find((k) => k.value === idKassa)?.label || "";

  const visibleLines = useMemo(() => {
    const lines = preview?.lines || [];
    if (isPayMode(mode)) return lines.filter((x) => x.symaDolg > EPS);
    if (mode === "return" && returnKind === "lines") return lines.filter((x) => x.symaPlat > EPS);
    return [];
  }, [preview, mode, returnKind]);

  const selectedSum = useMemo(
    () =>
      round2(
        visibleLines
          .filter((x) => checked.includes(x.idStr))
          .reduce((sum, x) => sum + (toNumber(amountByStr[x.idStr]) ?? 0), 0)
      ),
    [visibleLines, checked, amountByStr]
  );

  const avansUse = useMemo(() => {
    if (!isPayMode(mode) || !useAvans) return 0;
    return round2(Math.min(preview?.avansOpen ?? 0, selectedSum));
  }, [mode, useAvans, preview, selectedSum]);

  const cashNeed = useMemo(
    () => (isPayMode(mode) ? round2(Math.max(0, selectedSum - avansUse)) : 0),
    [mode, selectedSum, avansUse]
  );

  useEffect(() => {
    if (!isPayMode(mode)) return;
    setSyma(cashNeed);
  }, [mode, cashNeed]);

  // Зачёт аванса без доплаты кассу не требует: сервер проведёт его по кассе, на которую принимали аванс.
  const kassaRequired = !isPayMode(mode) || cashNeed > EPS;

  const toggleLine = (idStr, value) => {
    setChecked((prev) => {
      if (value) return prev.includes(idStr) ? prev : [...prev, idStr];
      return prev.filter((x) => x !== idStr);
    });
  };

  const changeAmount = (idStr, value) => {
    setAmountByStr((prev) => ({ ...prev, [idStr]: value }));
  };

  const selectedItems = () =>
    visibleLines
      .filter((x) => checked.includes(x.idStr))
      .map((x) => ({
        idCnt: x.idStr,
        syma: round2(toNumber(amountByStr[x.idStr]) ?? 0)
      }))
      .filter((x) => x.syma > EPS);

  const commonBody = () => ({
    idKassa: kassaRequired ? idKassa : null,
    fdate: fdate && dayjs(fdate).isValid() ? dayjs(fdate).startOf("day").toISOString() : null,
    komment: komment || "",
    txtOsn: txtOsn || ""
  });

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const onSubmit = async () => {
    if (kassaRequired && !idKassa) {
      message.error("Выберите кассу");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      let result;
      if (mode === "advance") {
        const amount = round2(syma);
        if (amount <= EPS) {
          message.error("Укажите сумму аванса");
          setSubmitting(false);
          return;
        }
        result = await docExpPayAdvance(idDoc, { ...commonBody(), syma: amount });
      } else if (isPayMode(mode)) {
        const items = selectedItems();
        if (!items.length) {
          message.error("Выберите услуги");
          setSubmitting(false);
          return;
        }
        if (avansUse > EPS) {
          result = await docExpPaySettle(idDoc, {
            ...commonBody(),
            syma: cashNeed,
            items
          });
        } else {
          result = await docExpPayLines(idDoc, { ...commonBody(), items });
        }
      } else if (returnKind === "advance") {
        const amount = round2(syma);
        if (amount <= EPS) {
          message.error("Укажите сумму возврата аванса");
          setSubmitting(false);
          return;
        }
        result = await docExpPayReturn(idDoc, { ...commonBody(), syma: amount, items: [] });
      } else {
        const items = selectedItems();
        if (!items.length) {
          message.error("Выберите услуги для возврата");
          setSubmitting(false);
          return;
        }
        result = await docExpPayReturn(idDoc, {
          ...commonBody(),
          syma: round2(syma),
          items
        });
      }

      if (result && (result.ok === false || result.Ok === false)) {
        throw new Error(result.message || result.Message || "Не удалось выполнить оплату");
      }

      message.success("Оплата выполнена");
      const doc = result?.doc ?? result?.Doc ?? null;
      onDone?.(doc);
    } catch (e) {
      setError(e.message || "Не удалось выполнить оплату");
    } finally {
      setSubmitting(false);
    }
  };

  const totals = isPayMode(mode) ? (
    <div className="doc-exp-pay__total">
      {avansUse > EPS ? (
        <>
          <span>
            Аванс: <b>{formatMoney(avansUse)}</b>
          </span>
          {cashNeed > EPS ? (
            <span>
              Доплатить: <b>{formatMoney(cashNeed)}</b>
            </span>
          ) : (
            <span>доплата не требуется</span>
          )}
        </>
      ) : (
        <span>
          К оплате: <b>{formatMoney(cashNeed)}</b>
        </span>
      )}
    </div>
  ) : mode === "return" && returnKind === "lines" ? (
    <div className="doc-exp-pay__total">
      <span>
        По позициям: <b>{formatMoney(selectedSum)}</b>
      </span>
    </div>
  ) : (
    <div className="doc-exp-pay__total">
      <span>
        Сумма: <b>{formatMoney(syma)}</b>
      </span>
    </div>
  );

  const footer = (
    <div className="doc-exp-pay__footer">
      {totals}
      <div className="doc-exp-pay__footer-actions">
        <Button
          type="primary"
          className="doc-exp-pay__ok"
          onClick={onSubmit}
          loading={submitting}
          disabled={
            loading ||
            (isPayMode(mode) && selectedSum <= EPS) ||
            (kassaRequired && !idKassa)
          }
        >
          {meta.ok}
        </Button>
        <Button className="doc-exp-pay__cancel" onClick={handleClose} disabled={submitting}>
          Отмена
        </Button>
      </div>
    </div>
  );

  return (
    <Drawer
      placement="right"
      size={size}
      maxSize={maxAllowedWidth()}
      open={open}
      onClose={handleClose}
      destroyOnClose
      title={meta.title}
      rootClassName="doc-exp-pay"
      closable={false}
      extra={
        <button
          type="button"
          className="doc-exp-pay__close-btn"
          aria-label="Закрыть"
          title="Закрыть"
          disabled={submitting}
          onClick={handleClose}
        >
          <span className="doc-exp-pay__close">
            <img src={xSrc} alt="" />
          </span>
        </button>
      }
      footer={footer}
      maskClosable={!submitting}
      keyboard={!submitting}
      resizable={{
        onResize,
        onResizeEnd
      }}
    >
      {loading ? (
        <div className="doc-exp-pay__center">
          <Spin />
        </div>
      ) : (
        <div className="doc-exp-pay__body">
          {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} /> : null}

          {preview ? (
            <div className="doc-exp-pay__summary">
              <span>
                Начислено <b>{formatMoney(preview.syma)}</b>
              </span>
              <span>
                Оплачено <b>{formatMoney(preview.symaPlat)}</b>
              </span>
              <span>
                Аванс <b>{formatMoney(preview.avansOpen)}</b>
              </span>
              <span>
                Долг строк <b>{formatMoney(preview.lineDebt)}</b>
              </span>
            </div>
          ) : null}

          {isPayMode(mode) && preview?.avansOpen > EPS ? (
            <div className="doc-exp-pay__field">
              <Checkbox
                checked={useAvans}
                disabled={submitting}
                onChange={(e) => setUseAvans(e.target.checked)}
              >
                Зачесть аванс {formatMoney(preview.avansOpen)}
              </Checkbox>
              <div className="doc-exp-pay__hint">
                {useAvans
                  ? cashNeed > EPS
                    ? `Зачесть ${formatMoney(avansUse)}, доплатить по кассе ${formatMoney(cashNeed)}.`
                    : "Аванса для оплаты достаточно, доплата не требуется."
                  : `Оплатить по кассе ${formatMoney(cashNeed)}.`}
              </div>
            </div>
          ) : null}

          {kassaRequired ? (
            <div className="doc-exp-pay__field">
              <div className="doc-exp-pay__label">Касса</div>
              <Select
                value={idKassa}
                options={kassaOptions}
                onChange={setIdKassa}
                placeholder="Выберите кассу"
                style={{ width: "100%" }}
                disabled={submitting}
              />
              {isPayMode(mode) && cashNeed > EPS ? (
                <div className="doc-exp-pay__hint">
                  {avansUse > EPS ? "Доплатить по кассе" : "Оплатить по кассе"}{" "}
                  <b>{formatMoney(cashNeed)}</b>
                  {kassaName ? ` (${kassaName})` : ""}
                  {avansUse > EPS ? `. Авансом зачтётся ${formatMoney(avansUse)}.` : "."}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="doc-exp-pay__field">
            <div className="doc-exp-pay__label">Дата</div>
            <DatePicker
              value={fdate}
              format="DD.MM.YYYY"
              allowClear={false}
              style={{ width: "100%" }}
              disabled={submitting}
              onChange={setFdate}
            />
          </div>

          {mode === "advance" ? (
            <div className="doc-exp-pay__field">
              <div className="doc-exp-pay__label">Сумма аванса</div>
              <InputNumber
                min={0}
                precision={2}
                style={{ width: "100%" }}
                value={syma}
                disabled={submitting}
                onChange={(v) => setSyma(v ?? 0)}
              />
            </div>
          ) : null}

          {mode === "return" ? (
            <div className="doc-exp-pay__field">
              <div className="doc-exp-pay__label">Вид возврата</div>
              <Select
                value={returnKind}
                disabled={submitting}
                style={{ width: "100%" }}
                onChange={(value) => {
                  setReturnKind(value);
                  setTxtOsn(value === "advance" ? "Возврат аванса" : "Возврат услуг");
                  if (value === "advance") setSyma(round2(preview?.avansOpen ?? 0));
                  else setSyma(0);
                }}
                options={[
                  { value: "advance", label: "Возврат неразнесённого аванса" },
                  { value: "lines", label: "Возврат услуг (снять зачёт)" }
                ]}
              />
            </div>
          ) : null}

          {mode === "return" && returnKind === "advance" ? (
            <div className="doc-exp-pay__field">
              <div className="doc-exp-pay__label">Сумма возврата</div>
              <InputNumber
                min={0}
                max={preview?.avansOpen}
                precision={2}
                style={{ width: "100%" }}
                value={syma}
                disabled={submitting}
                onChange={(v) => setSyma(v ?? 0)}
              />
            </div>
          ) : null}

          {mode === "return" && returnKind === "lines" ? (
            <div className="doc-exp-pay__field">
              <div className="doc-exp-pay__label">Выдать гостю наличными</div>
              <InputNumber
                min={0}
                precision={2}
                style={{ width: "100%" }}
                value={syma}
                disabled={submitting}
                onChange={(v) => setSyma(v ?? 0)}
              />
              <div className="doc-exp-pay__hint">
                0 — только снять зачёт, деньги остаются открытым авансом
              </div>
            </div>
          ) : null}

          {visibleLines.length ? (
            <div className="doc-exp-pay__list">
              {visibleLines.map((item) => {
                const max = mode === "return" ? round2(item.symaPlat) : round2(item.symaDolg);
                return (
                  <div key={item.idStr} className="doc-exp-pay__item">
                    <Checkbox
                      checked={checked.includes(item.idStr)}
                      disabled={submitting}
                      onChange={(e) => toggleLine(item.idStr, e.target.checked)}
                    >
                      <span className="doc-exp-pay__name">{cntLineLabel(item)}</span>
                    </Checkbox>
                    <div className="doc-exp-pay__item-row">
                      <span className="doc-exp-pay__item-meta">
                        {mode === "return"
                          ? `Разнесено ${formatMoney(item.symaPlat)}`
                          : `Долг ${formatMoney(item.symaDolg)}`}
                      </span>
                      <InputNumber
                        min={0}
                        max={max}
                        precision={2}
                        size="small"
                        style={{ width: 120 }}
                        value={amountByStr[item.idStr]}
                        disabled={submitting || !checked.includes(item.idStr)}
                        onChange={(v) => changeAmount(item.idStr, v ?? 0)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : isPayMode(mode) ? (
            <div className="doc-exp-pay__empty">Нет строк с долгом</div>
          ) : mode === "return" && returnKind === "lines" ? (
            <div className="doc-exp-pay__empty">Нет разнесённых услуг</div>
          ) : null}

          <div className="doc-exp-pay__field">
            <div className="doc-exp-pay__label">Основание</div>
            <Input value={txtOsn} disabled={submitting} onChange={(e) => setTxtOsn(e.target.value)} />
          </div>
          <div className="doc-exp-pay__field">
            <div className="doc-exp-pay__label">Комментарий</div>
            <Input.TextArea
              rows={2}
              value={komment}
              disabled={submitting}
              onChange={(e) => setKomment(e.target.value)}
            />
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default DocExpPayDrawer;
